import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Lock, RefreshCw, Check, X, Clock, CreditCard, CheckCircle2, XCircle, Calendar, Users, Phone, Mail, Save, Settings, ListChecks, BarChart3, TrendingUp, RotateCcw, Wallet,
} from "lucide-react";
import { ACTIVITIES } from "../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACT_OPTIONS = {};
ACTIVITIES.forEach((a) => { ACT_OPTIONS[a.id] = a.options || []; });

const STATUS_META = {
  pending_payment: { label: "En attente de paiement", cls: "bg-[#efe7d9] text-[#8a6d3b]", icon: Clock },
  deposit_paid: { label: "Acompte payé · à traiter", cls: "bg-[#dfeaf2] text-[#38617d]", icon: CreditCard },
  confirmed: { label: "Confirmée", cls: "bg-[#e0ece0] text-[#3f6b3f]", icon: CheckCircle2 },
  date_proposed: { label: "Nouvelle date proposée", cls: "bg-[#efe7d9] text-[#8a6d3b]", icon: Calendar },
  cancelled: { label: "Annulée (remboursée)", cls: "bg-[#f2e0e0] text-[#8a4b4b]", icon: XCircle },
  rejected: { label: "Refusée", cls: "bg-[#f2e0e0] text-[#8a4b4b]", icon: XCircle },
};

function balanceText(r) {
  const total = r.total_eur != null ? r.total_eur : (r.option_eur ? r.option_eur * (r.people || 1) : null);
  if (total == null) return "à confirmer";
  const bal = Math.max(0, Math.round((total - (r.deposit_total_eur || 0)) * 100) / 100);
  return `${bal} €`;
}

const Admin = () => {
  const [pwd, setPwd] = useState(localStorage.getItem("av_admin") || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("requests");
  const [config, setConfig] = useState([]);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadStats = useCallback(async (password) => {
    try {
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { "X-Admin-Password": password },
      });
      setStats(res.data);
    } catch (e) {}
  }, []);
  const [savedMsg, setSavedMsg] = useState("");

  const loadConfig = useCallback(async (password) => {
    try {
      const res = await axios.get(`${API}/admin/deposit-config`, {
        headers: { "X-Admin-Password": password },
      });
      setConfig(res.data);
    } catch (e) {}
  }, []);

  const setConfigField = (activityId, field, value) => {
    setConfig((c) => c.map((x) => (x.activity_id === activityId ? { ...x, [field]: value } : x)));
  };

  const setConfigOption = (activityId, idx, field, value) => {
    setConfig((c) => c.map((x) => {
      if (x.activity_id !== activityId) return x;
      const options = { ...(x.options || {}) };
      const cur = options[String(idx)] || { type: x.type, value: x.value };
      options[String(idx)] = { ...cur, [field]: value };
      return { ...x, options };
    }));
  };

  const saveConfig = async () => {
    setSaving(true);
    setSavedMsg("");
    try {
      await axios.put(
        `${API}/admin/deposit-config`,
        {
          items: config.map((c) => ({
            activity_id: c.activity_id,
            type: c.type,
            value: Number(c.value) || 0,
            address: c.address || "",
            options: c.options
              ? Object.entries(c.options).map(([index, o]) => ({
                  index: Number(index),
                  type: o.type,
                  value: Number(o.value) || 0,
                }))
              : undefined,
          })),
        },
        { headers: { "X-Admin-Password": pwd } }
      );
      setSavedMsg("Enregistré ✓");
      setTimeout(() => setSavedMsg(""), 2500);
    } catch (e) {
      setSavedMsg("Erreur d'enregistrement");
    }
    setSaving(false);
  };

  const load = useCallback(async (password) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/prebookings`, {
        headers: { "X-Admin-Password": password },
      });
      setRows(res.data);
      setAuthed(true);
      localStorage.setItem("av_admin", password);
      loadConfig(password);
      loadStats(password);
    } catch (e) {
      setError("Mot de passe incorrect ou erreur serveur.");
      setAuthed(false);
    }
    setLoading(false);
  }, [loadConfig, loadStats]);

  useEffect(() => {
    if (pwd) load(pwd);
  }, [pwd, load]);

  const login = (e) => {
    e.preventDefault();
    setPwd(input);
  };

  const [actionState, setActionState] = useState({ id: null, mode: null, date: "", time: "", message: "", busy: false });

  const doAction = async (id, action, extra = {}) => {
    setActionState((s) => ({ ...s, busy: true }));
    try {
      const res = await axios.post(
        `${API}/admin/prebookings/${id}/action`,
        { action, ...extra },
        { headers: { "X-Admin-Password": pwd } }
      );
      setRows((r) => r.map((x) => (x.id === id ? { ...x, status: res.data.status, ...(extra.proposed_date ? { proposed_date: extra.proposed_date } : {}) } : x)));
      setActionState({ id: null, mode: null, date: "", time: "", message: "", busy: false });
      if (res.data.email_configured === false) {
        alert("Action enregistrée. (L'email n'a pas été envoyé : SMTP pas encore configuré.)");
      }
    } catch (e) {
      setActionState((s) => ({ ...s, busy: false }));
      alert("Erreur lors de l'action.");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#1c221e] flex items-center justify-center px-5">
        <form onSubmit={login} className="bg-[#f7f5ef] rounded-2xl p-8 w-full max-w-sm">
          <div className="w-14 h-14 rounded-full bg-[#7c8f74]/15 flex items-center justify-center mx-auto mb-5">
            <Lock size={24} className="text-[#5c6b54]" />
          </div>
          <h1 className="font-serif text-3xl text-center text-[#20261f] mb-1">Espace Admin</h1>
          <p className="text-center text-sm text-[#6b7166] mb-6">AGADIR VIBES · Réservations</p>
          <input
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Mot de passe"
            className="w-full bg-white border border-[#dedbd0] rounded-lg px-4 py-3 mb-3 outline-none focus:border-[#7c8f74]"
          />
          {error && <p className="text-sm text-[#b04a4a] mb-3">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#7c8f74] hover:bg-[#697c62] text-white rounded-full py-3 text-sm tracking-wide uppercase transition"
          >
            {loading ? "…" : "Se connecter"}
          </button>
        </form>
      </div>
    );
  }

  const counts = rows.reduce((a, r) => ((a[r.status] = (a[r.status] || 0) + 1), a), {});

  return (
    <div className="min-h-screen bg-[#eef0e9] pt-8 pb-16 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="font-serif text-4xl text-[#20261f]">Espace Admin</h1>
            <p className="text-sm text-[#6b7166] mt-1">
              {rows.length} réservation(s) · {counts.deposit_paid || 0} à valider
            </p>
          </div>
          <button
            onClick={() => load(pwd)}
            className="inline-flex items-center gap-2 bg-white border border-[#dcdccf] px-4 py-2.5 rounded-full text-sm text-[#3d443a] hover:border-[#7c8f74] transition"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Actualiser
          </button>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setTab("requests")}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition ${
              tab === "requests" ? "bg-[#7c8f74] text-white" : "bg-white text-[#3d443a] border border-[#dcdccf]"
            }`}
          >
            <ListChecks size={16} /> Réservations
          </button>
          <button
            onClick={() => setTab("settings")}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition ${
              tab === "settings" ? "bg-[#7c8f74] text-white" : "bg-white text-[#3d443a] border border-[#dcdccf]"
            }`}
          >
            <Settings size={16} /> Acomptes
          </button>
          <button
            onClick={() => { setTab("stats"); loadStats(pwd); }}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition ${
              tab === "stats" ? "bg-[#7c8f74] text-white" : "bg-white text-[#3d443a] border border-[#dcdccf]"
            }`}
          >
            <BarChart3 size={16} /> Statistiques
          </button>
        </div>

        {tab === "stats" ? (
          <div className="space-y-6">
            {!stats ? (
              <div className="bg-white rounded-2xl p-16 text-center text-[#6b7166] border border-[#e2e0d6]">
                Chargement des statistiques…
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { label: "Net encaissé", value: `${stats.net_collected_eur} €`, icon: Wallet, hint: "acomptes − remboursements" },
                    { label: "Total encaissé", value: `${stats.total_collected_eur} €`, icon: TrendingUp, hint: `${stats.paid_reservations} paiement(s)` },
                    { label: "Remboursé", value: `${stats.total_refunded_eur} €`, icon: RotateCcw, hint: "annulations" },
                    { label: "À encaisser sur place", value: `${stats.balance_onsite_eur} €`, icon: CreditCard, hint: "réservations confirmées" },
                  ].map((k, i) => {
                    const Icon = k.icon;
                    return (
                      <div key={i} className="bg-white rounded-2xl border border-[#e2e0d6] p-5">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs uppercase tracking-wide text-[#9aa08f]">{k.label}</span>
                          <Icon size={18} className="text-[#7c8f74]" />
                        </div>
                        <p className="font-serif text-3xl text-[#20261f]">{k.value}</p>
                        <p className="text-xs text-[#9aa08f] mt-1">{k.hint}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-2xl border border-[#e2e0d6] p-6">
                    <h3 className="font-serif text-2xl text-[#20261f] mb-4">Réservations par statut</h3>
                    <div className="space-y-2">
                      {Object.entries(stats.by_status).length === 0 && (
                        <p className="text-sm text-[#6b7166]">Aucune donnée.</p>
                      )}
                      {Object.entries(stats.by_status).map(([st, n]) => {
                        const meta = STATUS_META[st] || { label: st, cls: "bg-[#eee] text-[#555]" };
                        return (
                          <div key={st} className="flex items-center justify-between">
                            <span className={`inline-flex items-center text-[11px] font-semibold px-3 py-1 rounded-full ${meta.cls}`}>{meta.label}</span>
                            <span className="font-medium text-[#20261f]">{n}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-4 pt-4 border-t border-[#f0eee5] flex items-center justify-between">
                      <span className="text-sm text-[#6b7166]">Total réservations</span>
                      <span className="font-serif text-xl text-[#20261f]">{stats.total_reservations}</span>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl border border-[#e2e0d6] p-6">
                    <h3 className="font-serif text-2xl text-[#20261f] mb-4">Par activité (payées)</h3>
                    {stats.per_activity.filter((a) => a.count > 0).length === 0 ? (
                      <p className="text-sm text-[#6b7166]">Aucune réservation payée pour l'instant.</p>
                    ) : (
                      <div className="space-y-3">
                        {stats.per_activity.filter((a) => a.count > 0).map((a) => {
                          const max = Math.max(...stats.per_activity.map((x) => x.count), 1);
                          return (
                            <div key={a.activity_id}>
                              <div className="flex items-center justify-between text-sm mb-1">
                                <span className="text-[#20261f]">{a.title_fr}</span>
                                <span className="text-[#6b7166]">{a.count} · {a.deposits_eur} €</span>
                              </div>
                              <div className="h-2 bg-[#eef0e9] rounded-full overflow-hidden">
                                <div className="h-full bg-[#7c8f74] rounded-full" style={{ width: `${(a.count / max) * 100}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        ) : tab === "settings" ? (
          <div className="bg-white rounded-2xl border border-[#e2e0d6] p-5 md:p-7">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-serif text-2xl text-[#20261f]">Réglage des acomptes</h2>
                <p className="text-sm text-[#6b7166]">
                  Choisissez pour chaque activité un montant fixe (€) ou un pourcentage (%) du prix de la formule.
                </p>
              </div>
              <div className="flex items-center gap-3">
                {savedMsg && <span className="text-sm text-[#5c6b54]">{savedMsg}</span>}
                <button
                  onClick={saveConfig}
                  disabled={saving}
                  className="inline-flex items-center gap-2 bg-[#7c8f74] hover:bg-[#697c62] text-white px-5 py-2.5 rounded-full text-sm transition"
                >
                  <Save size={15} /> {saving ? "…" : "Enregistrer"}
                </button>
              </div>
            </div>
            <div className="divide-y divide-[#f0eee5]">
              {config.map((c) => (
                <div key={c.activity_id} className="py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <span className="text-[#20261f] font-medium">{c.title_fr}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#9aa08f] mr-1">
                        {(ACT_OPTIONS[c.activity_id] || []).length > 1 ? "Par défaut" : "Acompte"}
                      </span>
                      <select
                        value={c.type}
                        onChange={(e) => setConfigField(c.activity_id, "type", e.target.value)}
                        className="bg-[#f7f5ef] border border-[#dedbd0] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#7c8f74]"
                      >
                        <option value="fixed">Montant fixe (€)</option>
                        <option value="percent">Pourcentage (%)</option>
                      </select>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={c.value}
                          onChange={(e) => setConfigField(c.activity_id, "value", e.target.value)}
                          className="w-24 bg-white border border-[#dedbd0] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#7c8f74]"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9aa08f] text-sm pointer-events-none">
                          {c.type === "percent" ? "%" : "€"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={c.address || ""}
                    placeholder="Adresse / point de RDV (affiché dans l'email de confirmation)"
                    onChange={(e) => setConfigField(c.activity_id, "address", e.target.value)}
                    className="mt-2 w-full bg-white border border-[#dedbd0] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#7c8f74]"
                  />

                  {(ACT_OPTIONS[c.activity_id] || []).length > 1 && (
                    <div className="mt-3 bg-[#f7f5ef] rounded-xl p-3 border border-[#eceae1]">
                      <p className="text-xs uppercase tracking-wide text-[#9aa08f] mb-2">Acompte par formule (optionnel)</p>
                      <div className="space-y-2">
                        {(ACT_OPTIONS[c.activity_id] || []).map((opt, idx) => {
                          const ov = c.options ? c.options[String(idx)] : null;
                          const ot = ov?.type || c.type;
                          const ovVal = ov && ov.value != null ? ov.value : c.value;
                          return (
                            <div key={idx} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                              <span className="text-sm text-[#4a5045]">
                                {opt.label.fr} <span className="text-[#9aa08f]">· {opt.eur}€</span>
                              </span>
                              <div className="flex items-center gap-2">
                                <select
                                  value={ot}
                                  onChange={(e) => setConfigOption(c.activity_id, idx, "type", e.target.value)}
                                  className="bg-white border border-[#dedbd0] rounded-lg px-2 py-1.5 text-sm outline-none focus:border-[#7c8f74]"
                                >
                                  <option value="fixed">Fixe (€)</option>
                                  <option value="percent">% </option>
                                </select>
                                <div className="relative">
                                  <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={ovVal}
                                    onChange={(e) => setConfigOption(c.activity_id, idx, "value", e.target.value)}
                                    className="w-20 bg-white border border-[#dedbd0] rounded-lg px-2 py-1.5 text-sm outline-none focus:border-[#7c8f74]"
                                  />
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9aa08f] text-xs pointer-events-none">
                                    {ot === "percent" ? "%" : "€"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-2xl p-16 text-center text-[#6b7166] border border-[#e2e0d6]">
            Aucune réservation pour le moment.
          </div>
        ) : (
          <div className="space-y-4">
            {rows.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.pending_payment;
              const Icon = meta.icon;
              return (
                <div key={r.id} className="bg-white rounded-2xl border border-[#e2e0d6] p-5 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap mb-2">
                        <h3 className="font-serif text-2xl text-[#20261f]">{r.activity_title}</h3>
                        <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1 rounded-full ${meta.cls}`}>
                          <Icon size={12} /> {meta.label}
                        </span>
                      </div>
                      {r.option_label && (
                        <p className="text-sm text-[#6b7166] mb-3">
                          Formule : {r.option_label} {r.option_eur ? `· ${r.option_eur}€` : ""}
                        </p>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm text-[#4a5045]">
                        <span className="flex items-center gap-2"><Users size={14} className="text-[#7c8f74]" /> {r.people} pers.{r.children ? ` (${r.children} enf.)` : ""}</span>
                        <span className="flex items-center gap-2"><Calendar size={14} className="text-[#7c8f74]" /> {r.date || "—"}{r.time ? ` · ${r.time}` : ""}</span>
                        <span className="flex items-center gap-2 font-medium"><CreditCard size={14} className="text-[#7c8f74]" /> Acompte {r.deposit_total_eur}€</span>
                        <span className="flex items-center gap-2 font-medium">💶 Reste : {balanceText(r)}</span>
                        {r.total_eur != null && <span className="flex items-center gap-2">🧾 Total {r.total_eur}€</span>}
                        <span className="flex items-center gap-2"><Phone size={14} className="text-[#7c8f74]" /> {r.phone}</span>
                        <span className="flex items-center gap-2 truncate"><Mail size={14} className="text-[#7c8f74]" /> {r.email}</span>
                        <span className="flex items-center gap-2">👤 {r.name}</span>
                      </div>
                      {r.addons && r.addons.length > 0 && (
                        <p className="mt-2 text-sm text-[#4a5045]">
                          Options : {r.addons.map((a) => `${a.label} (+${a.eur}€)`).join(", ")}
                        </p>
                      )}
                      {r.proposed_date && r.status === "date_proposed" && (
                        <p className="mt-3 text-sm text-[#8a6d3b] bg-[#f6efe2] rounded-lg px-3 py-2">
                          Date proposée au client : <strong>{r.proposed_date}{r.proposed_time ? ` · ${r.proposed_time}` : ""}</strong>
                        </p>
                      )}
                      {r.message && (
                        <p className="mt-3 text-sm text-[#6b7166] bg-[#f5f4ee] rounded-lg px-3 py-2 italic">"{r.message}"</p>
                      )}
                    </div>

                    {["deposit_paid", "date_proposed"].includes(r.status) && (
                      <div className="flex flex-col gap-2 shrink-0 md:w-52">
                        <button
                          onClick={() => doAction(r.id, "confirm")}
                          disabled={actionState.busy}
                          className="inline-flex items-center justify-center gap-1.5 bg-[#7c8f74] hover:bg-[#697c62] text-white px-4 py-2.5 rounded-full text-sm transition"
                        >
                          <Check size={15} /> Valider
                        </button>
                        <button
                          onClick={() => setActionState({ id: r.id, mode: "propose", date: "", time: "", message: "", busy: false })}
                          className="inline-flex items-center justify-center gap-1.5 border border-[#dcdccf] text-[#3d443a] px-4 py-2.5 rounded-full text-sm hover:border-[#7c8f74] transition"
                        >
                          <Calendar size={15} /> Proposer une date
                        </button>
                        <button
                          onClick={() => setActionState({ id: r.id, mode: "cancel", date: "", time: "", message: "", busy: false })}
                          className="inline-flex items-center justify-center gap-1.5 border border-[#dcdccf] text-[#8a4b4b] px-4 py-2.5 rounded-full text-sm hover:border-[#b04a4a] transition"
                        >
                          <X size={15} /> Annuler
                        </button>
                      </div>
                    )}
                  </div>

                  {actionState.id === r.id && actionState.mode === "propose" && (
                    <div className="mt-4 border-t border-[#f0eee5] pt-4 flex flex-col sm:flex-row gap-3 sm:items-end">
                      <div className="w-full sm:w-40">
                        <label className="block text-xs text-[#6b7166] mb-1">Nouvelle date</label>
                        <input
                          type="date"
                          value={actionState.date}
                          onChange={(e) => setActionState((s) => ({ ...s, date: e.target.value }))}
                          className="w-full bg-[#f7f5ef] border border-[#dedbd0] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#7c8f74]"
                        />
                      </div>
                      <div className="w-full sm:w-32">
                        <label className="block text-xs text-[#6b7166] mb-1">Créneau (heure)</label>
                        <input
                          type="time"
                          value={actionState.time}
                          onChange={(e) => setActionState((s) => ({ ...s, time: e.target.value }))}
                          className="w-full bg-[#f7f5ef] border border-[#dedbd0] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#7c8f74]"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs text-[#6b7166] mb-1">Message (optionnel)</label>
                        <input
                          type="text"
                          value={actionState.message}
                          onChange={(e) => setActionState((s) => ({ ...s, message: e.target.value }))}
                          className="w-full bg-white border border-[#dedbd0] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#7c8f74]"
                        />
                      </div>
                      <button
                        onClick={() => actionState.date && doAction(r.id, "propose", { proposed_date: actionState.date, proposed_time: actionState.time || undefined, message: actionState.message, origin_url: window.location.origin })}
                        disabled={!actionState.date || actionState.busy}
                        className="bg-[#7c8f74] hover:bg-[#697c62] disabled:opacity-50 text-white px-5 py-2.5 rounded-full text-sm transition whitespace-nowrap"
                      >
                        Envoyer la proposition
                      </button>
                    </div>
                  )}

                  {actionState.id === r.id && actionState.mode === "cancel" && (
                    <div className="mt-4 border-t border-[#f0eee5] pt-4">
                      <p className="text-sm text-[#8a4b4b] mb-3">
                        Annuler cette réservation ? L'acompte de {r.deposit_total_eur}€ sera remboursé automatiquement via Stripe et le client recevra un email.
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Message au client (optionnel)"
                          value={actionState.message}
                          onChange={(e) => setActionState((s) => ({ ...s, message: e.target.value }))}
                          className="flex-1 bg-white border border-[#dedbd0] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#7c8f74]"
                        />
                        <button
                          onClick={() => doAction(r.id, "cancel", { message: actionState.message })}
                          disabled={actionState.busy}
                          className="bg-[#b04a4a] hover:bg-[#8a4b4b] text-white px-5 py-2.5 rounded-full text-sm transition whitespace-nowrap"
                        >
                          Confirmer l'annulation
                        </button>
                        <button
                          onClick={() => setActionState({ id: null, mode: null, date: "", message: "", busy: false })}
                          className="border border-[#dcdccf] text-[#3d443a] px-4 py-2.5 rounded-full text-sm"
                        >
                          Retour
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
